import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, Calculator } from "lucide-react";
import DailySummaryReport from "./DailySummaryReport";
import EndOfDayCashCount from "./EndOfDayCashCount";
import { useStock } from "@/context/StockContext";
import { useRepairShopContext } from "@/context/RepairShopContext";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { CashCountFormData } from "@/types/stock";
import { toast } from "sonner";
import { format } from "date-fns";

interface ReportsTabProps {
  viewMode: "grid" | "list";
  touchMode: boolean;
  onViewModeChange: (mode: "grid" | "list") => void;
}

const ReportsTab: React.FC<ReportsTabProps> = ({ touchMode }) => {
  const { t } = useTranslation();
  const { sales } = useStock();
  const { repairShop } = useRepairShopContext();
  const { user } = useAuth();
  const [activeReportTab, setActiveReportTab] = useState("summary");

  // Calculate expected cash from today's cash sales
  const expectedCash = useMemo(() => {
    const today = format(new Date(), "yyyy-MM-dd");
    return sales
      .filter(
        (sale) =>
          sale.paymentMethod === "cash" &&
          format(new Date(sale.createdAt), "yyyy-MM-dd") === today
      )
      .reduce((sum, sale) => sum + sale.totalAmount, 0);
  }, [sales]);

  const handleSaveCashCount = async (data: CashCountFormData) => {
    if (!user || !repairShop) {
      throw new Error("User or repair shop not found");
    }

    try {
      // Calculate counted cash
      const countedCash =
        data.count50 * 50 +
        data.count20 * 20 +
        data.count10 * 10 +
        data.count5 * 5 +
        data.count1 * 1 +
        data.count0_500 * 0.5 +
        data.count0_200 * 0.2 +
        data.count0_100 * 0.1 +
        data.count0_050 * 0.05;

      const difference = countedCash - expectedCash;

      const { error } = await supabase.from("cash_counts").insert({
        count_date: format(data.countDate, "yyyy-MM-dd"),
        count_50: data.count50,
        count_20: data.count20,
        count_10: data.count10,
        count_5: data.count5,
        count_1: data.count1,
        count_0_500: data.count0_500,
        count_0_200: data.count0_200,
        count_0_100: data.count0_100,
        count_0_050: data.count0_050,
        counted_cash: countedCash,
        expected_cash: expectedCash,
        difference: difference,
        notes: data.notes,
        counted_by_user_id: user.id,
        repair_shop_id: repairShop.id,
      });

      if (error) throw error;
    } catch (error) {
      console.error("Error saving cash count:", error);
      throw error;
    }
  };

  return (
    <Tabs value={activeReportTab} onValueChange={setActiveReportTab}>
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="summary" className="flex items-center gap-2">
          <FileText className="h-4 w-4" />
          {t("stock.dailySummary")}
        </TabsTrigger>
        <TabsTrigger value="cashcount" className="flex items-center gap-2">
          <Calculator className="h-4 w-4" />
          {t("stock.cashCount.title")}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="summary" className="mt-6">
        <DailySummaryReport
          viewMode="grid"
          touchMode={touchMode}
          onViewModeChange={() => {}}
        />
      </TabsContent>

      <TabsContent value="cashcount" className="mt-6">
        <EndOfDayCashCount
          expectedCash={expectedCash}
          onSave={handleSaveCashCount}
          touchMode={touchMode}
        />
      </TabsContent>
    </Tabs>
  );
};

export default ReportsTab;

